import {
	addWeeks,
	differenceInMinutes,
	endOfWeek,
	eachDayOfInterval,
	format,
	isSameDay,
	isWithinInterval,
	parseISO,
	startOfWeek,
	subWeeks,
} from 'date-fns';

export const EDITABLE_ATTENDANCE_USER_IDS = [
	'6cdbf500-bc68-4745-b35f-17fe39f866b3',
	'f90f71e1-b965-4f4a-8a44-c38f890544d7',
];

const MAX_REASONABLE_SESSION_MINUTES = 18 * 60;

export function normalizeAttendanceResponse(payload) {
	if (Array.isArray(payload)) {
		return payload;
	}

	if (Array.isArray(payload?.items)) {
		return payload.items;
	}

	return [];
}

export function parseAttendanceDate(value) {
	if (!value) {
		return null;
	}

	const date =
		typeof value === 'string' ? parseISO(value) : new Date(value);

	return Number.isNaN(date.getTime()) ? null : date;
}

export function getRecordDate(record) {
	return (
		parseAttendanceDate(record?.checkInTime) ||
		parseAttendanceDate(record?.date) ||
		parseAttendanceDate(record?.checkOutTime)
	);
}

export function getDurationMinutes(record) {
	const checkIn = parseAttendanceDate(record?.checkInTime);
	const checkOut = parseAttendanceDate(record?.checkOutTime);

	if (!checkIn || !checkOut) {
		return null;
	}

	const durationMinutes = differenceInMinutes(checkOut, checkIn);

	if (
		durationMinutes < 0 ||
		durationMinutes > MAX_REASONABLE_SESSION_MINUTES
	) {
		return null;
	}

	return durationMinutes;
}

export function hasInvalidDuration(record) {
	return Boolean(record?.checkOutTime) && getDurationMinutes(record) === null;
}

export function formatDuration(minutes) {
	if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
		return '--';
	}

	const safeMinutes = Math.max(0, minutes);
	const hours = Math.floor(safeMinutes / 60);
	const remainingMinutes = safeMinutes % 60;

	return `${hours}h ${String(remainingMinutes).padStart(2, '0')}m`;
}

export function formatAttendanceDate(value) {
	const date = parseAttendanceDate(value);
	return date ? format(date, 'M/d/yyyy') : '--';
}

export function formatAttendanceTime(value) {
	const date = parseAttendanceDate(value);
	return date ? format(date, 'h:mm:ss a') : '--';
}

export function formatDateTimeLocalValue(value) {
	const date = parseAttendanceDate(value);
	return date ? format(date, "yyyy-MM-dd'T'HH:mm") : '';
}

export function getWeekRange(referenceDate = new Date()) {
	const weekStart = startOfWeek(referenceDate, { weekStartsOn: 1 });
	const weekEnd = endOfWeek(referenceDate, { weekStartsOn: 1 });

	return { weekStart, weekEnd };
}

export function getPreviousWeek(date) {
	return subWeeks(date, 1);
}

export function getNextWeek(date) {
	return addWeeks(date, 1);
}

export function getWeekLabel(weekStart) {
	const { weekEnd } = getWeekRange(weekStart);
	return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
}

export function getWeekDays(weekStart) {
	const { weekEnd } = getWeekRange(weekStart);

	return eachDayOfInterval({
		start: weekStart,
		end: weekEnd,
	});
}

export function isRecordOnDay(record, date) {
	const recordDate = getRecordDate(record);
	return recordDate ? isSameDay(recordDate, date) : false;
}

export function isRecordInWeek(record, weekStart) {
	const recordDate = getRecordDate(record);
	const { weekEnd } = getWeekRange(weekStart);

	return recordDate
		? isWithinInterval(recordDate, { start: weekStart, end: weekEnd })
		: false;
}

export function sortAttendanceRecords(records = [], direction = 'desc') {
	const sorted = [...records].sort((left, right) => {
		const leftDate = getRecordDate(left);
		const rightDate = getRecordDate(right);
		const leftTime = leftDate ? leftDate.getTime() : 0;
		const rightTime = rightDate ? rightDate.getTime() : 0;

		return rightTime - leftTime;
	});

	return direction === 'asc' ? sorted.reverse() : sorted;
}

export function sumRecordMinutes(records = []) {
	return records.reduce((total, record) => {
		const durationMinutes = getDurationMinutes(record);
		return durationMinutes === null ? total : total + durationMinutes;
	}, 0);
}

export function getDailyAttendanceSummary(records = [], day) {
	const dailyRecords = sortAttendanceRecords(
		records.filter((record) => isRecordOnDay(record, day)),
		'asc'
	);
	const workRecords = dailyRecords.filter((record) => !record?.isBreak);
	const breakRecords = dailyRecords.filter((record) => record?.isBreak);
	const firstClockIn = workRecords[0]?.checkInTime || null;
	const lastClockOut =
		[...workRecords]
			.reverse()
			.find((record) => record?.checkOutTime)?.checkOutTime || null;

	return {
		date: day,
		records: dailyRecords,
		workRecords,
		breakRecords,
		workMinutes: sumRecordMinutes(workRecords),
		breakMinutes: sumRecordMinutes(breakRecords),
		breakCount: breakRecords.length,
		firstClockIn,
		lastClockOut,
		hasInvalidRecords: dailyRecords.some(hasInvalidDuration),
		openWorkRecord: workRecords.find((record) => !record?.checkOutTime) || null,
		openBreakRecord:
			breakRecords.find((record) => !record?.checkOutTime) || null,
	};
}

export function buildWeekSummaries(records = [], weekStart) {
	return getWeekDays(weekStart).map((day) =>
		getDailyAttendanceSummary(records, day)
	);
}

export function getWeeklyTotals(records = []) {
	const workRecords = records.filter((record) => !record?.isBreak);
	const breakRecords = records.filter((record) => record?.isBreak);
	const paidMinutes = sumRecordMinutes(workRecords);
	const breakMinutes = sumRecordMinutes(breakRecords);
	const overtimeMinutes = Math.max(paidMinutes - 40 * 60, 0);

	return {
		paidMinutes,
		breakMinutes,
		breakCount: breakRecords.length,
		regularMinutes: Math.min(paidMinutes, 40 * 60),
		overtimeMinutes,
		remainingMinutes: Math.max(40 * 60 - paidMinutes, 0),
	};
}
